import React from 'react'
import FitToPageButton from './FitToPageButton'

/**
 * Freehand Element Properties Component
 * Handles the properties panel for freehand drawing elements
 */
const FreehandElementProperties = ({
  element,
  selectedElement,
  updateElementProperty,
  currentPage
}) => {
  if (element.type !== 'freehand') {
    return null
  }

  // Helper function to update stroke properties
  const updateStrokeProperty = (property, value) => {
    const currentStroke = element.stroke || {}
    const newStroke = { ...currentStroke, [property]: value }
    updateElementProperty(selectedElement, 'stroke', newStroke)
  }

  // Helper function to update path properties
  const updatePathProperty = (property, value) => {
    const currentPathData = element.pathData || {}
    const newPathData = { ...currentPathData, [property]: value }
    updateElementProperty(selectedElement, 'pathData', newPathData)
  }

  // Function to toggle closed shape
  const toggleClosedShape = () => {
    const pathData = element.pathData || {}
    const nodes = pathData.nodes || []
    const segments = pathData.segments || []

    if (pathData.closed) {
      // Open the shape - remove the closing segment
      const newSegments = segments.filter(segment =>
        !(segment.from === nodes.length - 1 && segment.to === 0) &&
        !(segment.to === 0 && segment.from === nodes.length - 1)
      )

      const newPathData = {
        ...pathData,
        closed: false,
        segments: newSegments,
        fill: null,
        fillOpacity: undefined
      }

      updateElementProperty(selectedElement, 'pathData', newPathData)
    } else {
      // Close the shape - add a segment from last to first node
      if (nodes.length > 2) {
        const newSegments = [...segments, {
          type: 'line',
          from: nodes.length - 1,
          to: 0
        }]

        const newPathData = {
          ...pathData,
          closed: true,
          segments: newSegments,
          fill: '#ffffff',
          fillOpacity: 0.3
        }

        updateElementProperty(selectedElement, 'pathData', newPathData)
      }
    }
  }

  const stroke = element.stroke || {}

  return (
    <div className="space-y-4">
      {/* Position and Size */}
      <div className="bg-gray-50 p-3 rounded-lg">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Position & Size</h4>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">X Position</label>
            <input
              type="number"
              value={Math.round(element.x || 0)}
              onChange={(e) => updateElementProperty(selectedElement, 'x', parseInt(e.target.value) || 0)}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Y Position</label>
            <input
              type="number"
              value={Math.round(element.y || 0)}
              onChange={(e) => updateElementProperty(selectedElement, 'y', parseInt(e.target.value) || 0)}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Width</label>
            <input
              type="number"
              value={Math.round(element.width || 0)}
              onChange={(e) => updateElementProperty(selectedElement, 'width', Math.max(10, parseInt(e.target.value) || 10))}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="10"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Height</label>
            <input
              type="number"
              value={Math.round(element.height || 0)}
              onChange={(e) => updateElementProperty(selectedElement, 'height', Math.max(10, parseInt(e.target.value) || 10))}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="10"
            />
          </div>
        </div>

        {/* Fit to Page Button */}
        <div className="mt-3">
          <FitToPageButton
            selectedElement={selectedElement}
            updateElementProperty={updateElementProperty}
            currentPage={currentPage}
            element={element}
          />
        </div>
      </div>

      {/* Stroke Properties */}
      <div className="bg-gray-50 p-3 rounded-lg">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Stroke Properties</h4>
        
        {/* Stroke Color */}
        <div className="flex items-center space-x-4 mb-3">
          <label className="text-sm text-gray-600 min-w-[50px]">Color:</label>
          <div className="flex items-center space-x-2">
            <input
              type="color"
              value={stroke.color || '#000000'}
              onChange={(e) => updateStrokeProperty('color', e.target.value)}
              className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
            />
            <input
              type="text"
              value={stroke.color || '#000000'}
              onChange={(e) => updateStrokeProperty('color', e.target.value)}
              className="border border-gray-300 rounded px-2 py-1 text-sm w-20 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="#000000"
            />
          </div>
        </div>

        {/* Stroke Width */}
        <div className="flex items-center space-x-4 mb-3">
          <label className="text-sm text-gray-600 min-w-[50px]">Width:</label>
          <div className="flex items-center space-x-2 flex-1">
            <input
              type="range"
              min="0.5"
              max="50"
              step="0.5"
              value={stroke.width || 2}
              onChange={(e) => updateStrokeProperty('width', parseFloat(e.target.value))}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <input
              type="number"
              value={stroke.width || 2}
              onChange={(e) => updateStrokeProperty('width', Math.max(0.5, parseFloat(e.target.value) || 2))}
              className="border border-gray-300 rounded px-2 py-1 text-sm w-16 focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="0.5"
              max="50"
              step="0.5"
            />
            <span className="text-xs text-gray-500">px</span>
          </div>
        </div>

        {/* Quick Width Presets */}
        <div className="flex flex-wrap gap-1 mb-3">
          {[1, 2, 3, 4, 5, 8, 12, 16].map((width) => (
            <button
              key={width}
              onClick={() => updateStrokeProperty('width', width)}
              className={`px-2 py-1 text-xs border rounded ${
                (stroke.width || 2) === width
                  ? 'bg-blue-500 text-white border-blue-500'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {width}px
            </button>
          ))}
        </div>

        {/* Stroke Opacity */}
        <div className="flex items-center space-x-4 mb-3">
          <label className="text-sm text-gray-600 min-w-[50px]">Opacity:</label>
          <div className="flex items-center space-x-2 flex-1">
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={stroke.opacity || 1}
              onChange={(e) => updateStrokeProperty('opacity', parseFloat(e.target.value))}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <span className="text-xs text-gray-500 w-8">
              {Math.round((stroke.opacity || 1) * 100)}%
            </span>
          </div>
        </div>

        {/* Line Cap */}
        <div className="flex items-center space-x-4 mb-3">
          <label className="text-sm text-gray-600 min-w-[50px]">Line Cap:</label>
          <div className="flex space-x-2">
            {['round', 'butt', 'square'].map((cap) => (
              <button
                key={cap}
                onClick={() => updateStrokeProperty('lineCap', cap)}
                className={`px-3 py-1 text-xs border rounded capitalize ${
                  (stroke.lineCap || 'round') === cap
                    ? 'bg-blue-500 text-white border-blue-500'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {cap}
              </button>
            ))}
          </div>
        </div>

        {/* Line Join */}
        <div className="flex items-center space-x-4 mb-3">
          <label className="text-sm text-gray-600 min-w-[50px]">Line Join:</label>
          <div className="flex space-x-2">
            {['round', 'miter', 'bevel'].map((join) => (
              <button
                key={join}
                onClick={() => updateStrokeProperty('lineJoin', join)}
                className={`px-3 py-1 text-xs border rounded capitalize ${
                  (stroke.lineJoin || 'round') === join
                    ? 'bg-blue-500 text-white border-blue-500'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {join}
              </button>
            ))}
          </div>
        </div>

        {/* Line Style (Dash Pattern) */}
        <div className="flex items-center space-x-4">
          <label className="text-sm text-gray-600 min-w-[50px]">Line Style:</label>
          <div className="flex flex-wrap gap-2">
            {[
              { name: 'Solid', value: null, preview: '────────' },
              { name: 'Dashed', value: [8, 4], preview: '── ── ──' },
              { name: 'Dotted', value: [2, 2], preview: '· · · · · ·' },
              { name: 'Dash-Dot', value: [8, 4, 2, 4], preview: '── · ── ·' },
              { name: 'Long Dash', value: [12, 6], preview: '─── ───' }
            ].map((style) => (
              <button
                key={style.name}
                onClick={() => updateStrokeProperty('dash', style.value)}
                className={`px-2 py-1 text-xs border rounded ${
                  JSON.stringify(stroke.dash || null) === JSON.stringify(style.value)
                    ? 'bg-blue-500 text-white border-blue-500'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
                title={style.name}
              >
                <span className="font-mono text-xs">{style.preview}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Shape Properties */}
      <div className="bg-gray-50 p-3 rounded-lg">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Shape Properties</h4>

        {/* Convert to Closed Shape */}
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm text-gray-600">Closed Shape:</label>
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-500">
              {element.pathData?.closed ? 'Closed' : 'Open'}
            </span>
            <button
              onClick={() => toggleClosedShape()}
              className={`px-3 py-1 text-xs border rounded ${
                element.pathData?.closed
                  ? 'bg-red-500 text-white border-red-500 hover:bg-red-600'
                  : 'bg-green-500 text-white border-green-500 hover:bg-green-600'
              }`}
            >
              {element.pathData?.closed ? 'Open Shape' : 'Close Shape'}
            </button>
          </div>
        </div>

        {/* Fill Properties (only show if closed) */}
        {element.pathData?.closed && (
          <div className="space-y-3 border-t pt-3">
            <h5 className="text-sm font-medium text-gray-600">Fill Properties</h5>

            {/* Fill Color */}
            <div className="flex items-center space-x-4">
              <label className="text-sm text-gray-600 min-w-[50px]">Fill:</label>
              <div className="flex items-center space-x-2">
                <input
                  type="color"
                  value={element.pathData?.fill || '#ffffff'}
                  onChange={(e) => updatePathProperty('fill', e.target.value)}
                  className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={element.pathData?.fill || '#ffffff'}
                  onChange={(e) => updatePathProperty('fill', e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1 text-sm w-20 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="#ffffff"
                />
                <button
                  onClick={() => updatePathProperty('fill', null)}
                  className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50"
                  title="No Fill"
                >
                  None
                </button>
              </div>
            </div>

            {/* Fill Opacity */}
            <div className="flex items-center space-x-4">
              <label className="text-sm text-gray-600 min-w-[50px]">Fill Opacity:</label>
              <div className="flex items-center space-x-2 flex-1">
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={element.pathData?.fillOpacity || 1}
                  onChange={(e) => updatePathProperty('fillOpacity', parseFloat(e.target.value))}
                  className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <span className="text-xs text-gray-500 w-8">
                  {Math.round((element.pathData?.fillOpacity || 1) * 100)}%
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Layer */}
      <div className="bg-gray-50 p-3 rounded-lg">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Layer</h4>
        <div className="flex items-center space-x-4">
          <label className="text-sm text-gray-600">Layer:</label>
          <input
            type="number"
            value={element.layer || 0}
            onChange={(e) => updateElementProperty(selectedElement, 'layer', parseInt(e.target.value) || 0)}
            className="border border-gray-300 rounded px-2 py-1 text-sm w-16 focus:outline-none focus:ring-2 focus:ring-blue-500"
            min="0"
          />
        </div>
      </div>

      {/* Path Info */}
      <div className="bg-gray-50 p-3 rounded-lg">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Path Information</h4>
        <div className="text-xs text-gray-600 space-y-1">
          <div>Nodes: {element.pathData?.nodes?.length || 0}</div>
          <div>Segments: {element.pathData?.segments?.length || 0}</div>
          <div>Type: Freehand Drawing</div>
        </div>
      </div>
    </div>
  )
}

export default FreehandElementProperties
